const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-preferences`;

export interface EmailPreferenceLinkParams {
  email: string;
  group_id: number;
  expires: number;
  signature: string;
}

export interface EmailPreferenceGroup {
  id: string;
  sendgrid_group_id: number;
  name: string;
  description: string | null;
  slug: string;
  enabled: boolean;
}

export interface EmailPreferenceState {
  ok: true;
  email: string;
  globally_unsubscribed: boolean;
  delivery_suppressed: boolean;
  groups: EmailPreferenceGroup[];
}

export function readEmailPreferenceLinkParams(
  search: string,
): EmailPreferenceLinkParams | null {
  const query = new URLSearchParams(search);
  const email = (query.get("email") ?? "").trim().toLowerCase();
  const groupId = Number(query.get("group_id"));
  const expires = Number(query.get("expires"));
  const signature = query.get("signature") ?? "";
  if (
    !email ||
    !email.includes("@") ||
    !Number.isInteger(groupId) ||
    groupId <= 0 ||
    !Number.isInteger(expires) ||
    expires <= 0 ||
    !signature
  )
    return null;
  return { email, group_id: groupId, expires, signature };
}

async function post<T>(payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(FN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const result = (await response.json().catch(() => ({}))) as Record<
    string,
    unknown
  >;
  if (!response.ok || result.ok !== true) {
    const code =
      typeof result.error === "string"
        ? result.error
        : "preference_center_unavailable";
    throw new Error(code);
  }
  return result as T;
}

export function loadEmailPreferences(
  params: EmailPreferenceLinkParams,
): Promise<EmailPreferenceState> {
  return post<EmailPreferenceState>({ action: "resolve", ...params });
}

export function saveEmailPreferences(
  params: EmailPreferenceLinkParams,
  enabledGroupIds: number[],
): Promise<{ ok: true; provider_sync: "complete" | "pending" }> {
  return post({
    action: "save",
    ...params,
    enabled_group_ids: enabledGroupIds,
  });
}

export function unsubscribeFromAll(
  params: EmailPreferenceLinkParams,
): Promise<{ ok: true; provider_sync: "complete" | "pending" }> {
  return post({ action: "unsubscribe_all", ...params });
}
